/**
 * Client-side translation helpers that use the structured data from common utilities
 */
import { translateChildDescription } from '../../../common/utils/i18n/coreTranslationHelpers';
import friendlyTimeTranslated from '../../../common/utils/i18n/friendlyTimeTranslated';
import { normaliseToChildCounts, ChildTypeCountMap, ChildTypeCount } from './containerChildCounts';
import { ElementCountMap } from '../../../common/elements/elementCountsUtils';
import { CHILD_TYPE_ARRAY } from '../../../common/utils/childTypeConstants';
import { isEmpty } from 'lodash';
import i18next, { TOptions } from 'i18next';

// Translation function type used by these helpers (no default value parameter)
type TranslationFunction = (translationKey: string, context?: TOptions) => string;

// React-i18next style function for common helpers
type ReactI18nextTranslationFunction = (key: string, options?: { count?: number; [key: string]: unknown }) => string;

/**
 * Create a direct namespace translator that bypasses any scoped context
 * This uses the global i18next instance to access specific namespaces directly
 */
const createDirectNamespaceTranslator = (targetNamespace: string): TranslationFunction => {
    return (key: string, context?: TOptions): string => {
        const namespacedKey = key.includes(':') ? key : `${targetNamespace}:${key}`;
        const rawResult = (i18next.t as (key: string, options?: TOptions) => string)(namespacedKey, context);
        const result = typeof rawResult === 'string' ? rawResult : String(rawResult);

        // If i18next returns the key itself, treat it as missing and let callers decide on a fallback
        if (result === namespacedKey) {
            return '';
        }

        return result;
    };
};

/**
 * Convert TranslationFunction to ReactI18nextTranslationFunction format
 * Simple wrapper that doesn't make assumptions about namespaces
 */
const createReactI18nextTranslator = (translate: TranslationFunction): ReactI18nextTranslationFunction => {
    return (key: string, options?: { count?: number; [key: string]: unknown }) => {
        return translate(key, options as TOptions | undefined);
    };
};

/**
 * Get structured data for children description without parsing strings
 */
const getChildrenDescriptionData = (
    childrenTypes: ChildTypeCountMap,
    isBoardDuplicating: boolean,
): {
    type: 'empty' | 'duplicating' | 'children';
    children?: ChildTypeCount[];
} => {
    if (isBoardDuplicating) {
        return { type: 'duplicating' };
    }

    const childrenCount = CHILD_TYPE_ARRAY.reduce((acc, type) => {
        const count = childrenTypes[type] || 0;
        if (count === 0) return acc;
        return [...acc, { type, count }];
    }, [] as ChildTypeCount[]);

    if (isEmpty(childrenCount)) {
        return { type: 'empty' };
    }

    return { type: 'children', children: childrenCount };
};

/**
 * Get a translated description of element counts using proper translation patterns
 */
export const getTranslatedElementCounts = (
    elementCounts: Record<string, number>,
    translate: TranslationFunction,
    useConjunction: boolean = false,
): string => {
    const reactI18nextT = createReactI18nextTranslator(translate);

    // Use the common helper that properly handles all element types
    return translateChildDescription(reactI18nextT, elementCounts, useConjunction);
};

/**
 * Get a translated description of children elements in a container
 */
export const getTranslatedChildrenDescription = (
    elementCounts: ElementCountMap | ChildTypeCountMap | null | undefined,
    isBoardDuplicating: boolean,
    translate: TranslationFunction,
    useConjunction: boolean = true,
): string => {
    if (!elementCounts) {
        return '';
    }

    const childrenTypes = normaliseToChildCounts(elementCounts);
    const data = getChildrenDescriptionData(childrenTypes, isBoardDuplicating);
    const elementsTranslate = createDirectNamespaceTranslator('elements');

    if (data.type === 'empty') {     
        // Try board search specific empty state first, then general
        const boardSearchEmpty = elementsTranslate('states.boardSearch.empty');

        return boardSearchEmpty || elementsTranslate('states.empty');
    }

    if (data.type === 'duplicating') {
        return elementsTranslate('states.duplicating');
    }

    if (data.type === 'children' && data.children) {
        // Shorten document to doc if there are more than 2 children element types
        const useShortDocument = data.children.length > 2 && data.children.some(child => child.type === 'document');
        
        const childCounts = data.children.reduce<Record<string, number>>(
            (acc, { type, count }) => {
                // Use document_short when applicable
                const elementType = (useShortDocument && type === 'document') ? 'document_short' : type;
                acc[elementType] = count;
                return acc;
            },
            {},
        );

        return getTranslatedElementCounts(childCounts, translate, useConjunction);
    }

    return '';
};

export const getFriendlyTranslatedTime = (
    seconds: number | Date,
    showSuffix = false,
    // App locale preference (for example: en-au, fr-fr).
    locale?: string,
    fromTimestamp?: number,
    lessThanMinuteMessageFromComponent?: string,
): string => {
    const commonTranslate = createDirectNamespaceTranslator('common');

    // Create a react-i18next style function that uses the direct namespace translator
    const reactI18nextT = (key: string, options?: { count?: number; [key: string]: unknown }): string => {
        return commonTranslate(key, options as TOptions | undefined);
    };

    // friendlyTimeTranslated expects a numeric timestamp (milliseconds)
    const timestampMillis = seconds instanceof Date ? seconds.getTime() : seconds;

    // Use the direct namespace translator for the "just now" message
    const lessThanMinuteMessage =
        typeof lessThanMinuteMessageFromComponent === 'string'
            ? lessThanMinuteMessageFromComponent
            : commonTranslate('time.suffixes.justNow');

    return friendlyTimeTranslated({
        t: reactI18nextT,
        timestamp: timestampMillis,
        fromTimestamp,
        lessThanMinuteMessage,
        showSuffix,
        locale,
    });
};
